<template>
  <div class="tw-mb-6 tw-grid tw-grid-cols-9 tw-gap-6 max-xl:tw-grid-cols-10">
    <div class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10">
      <cl-data-card data-test-id="customer-emails-analyzed-card">
        <template #title>
          {{ $t("emails-analyzed-legend") }}
        </template>
        <template #footer>
          <cl-data-card-footer-loading v-if="loading" />
          <cl-data-card-footer-big-number v-else>
            <span :data-test-id="`cust-email-security-email-analyzed-count`">{{
              formatNumberCompat(totalEmailsAnalyzed)
            }}</span>
          </cl-data-card-footer-big-number>
        </template>
      </cl-data-card>
    </div>
    <div
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="'customer-overview-card-incidents-route'"
    >
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: `${CUSTOMER}-email_security-incidents` }"
        custom
      >
        <cl-data-card
          data-test-id="customer-incidents-card"
          :link="{ href, navigate, label: $t('incidents-legend') }"
        >
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span :data-test-id="`cust-email-security-incidents-count`">
                {{ formatNumberCompat(totalIncidents) }}
              </span>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
    <div
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="'customer-overview-card-active-users-licences-route'"
    >
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: `${CUSTOMER}UsageRoute` }"
        custom
      >
        <cl-data-card
          data-test-id="customer-active-users-card"
          :link="{
            href,
            navigate,
            label: $t(`active-users-licenses-purchased`),
          }"
        >
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span :data-test-id="`cust-email-security-active-users-count`">{{
                totalActiveUsers
              }}</span>
              <cl-data-card-footer-small-number>
                <span
                  :data-test-id="`cust-email-security-email-licences-purchased-count`"
                  >/{{ licencesPurchased }}</span
                >
              </cl-data-card-footer-small-number>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useHistoryStore } from "@/store/history.store";
import { useAzureStore } from "@/store/azure.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";

import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import dayjs from "dayjs";
import { CUSTOMER } from "@/constants";
import { formatNumberCompat } from "@/utils/formatNumberCompat";
import { ConsumerOrganisationLicencesResponse } from "@/types/licenceSolutions.ts";

const props = defineProps({
  startDate: {
    type: String,
    required: true,
  },
});

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();

const loading = ref(true);

const historyStore = useHistoryStore();
const azureStore = useAzureStore();
const licenceStore = useLicenceStore();
const { totalEmailsAnalyzed, totalIncidents } = storeToRefs(historyStore);
const { collection } = storeToRefs(licenceStore);

const licencesPurchased = computed(() => {
  return formatNumberCompat(allLicences.value?.seats_purchased ?? 0);
});

const allLicences = computed<ConsumerOrganisationLicencesResponse | null>(
  () => collection.value as ConsumerOrganisationLicencesResponse | null,
);

const totalActiveUsers = computed(() => {
  return formatNumberCompat(allLicences.value?.seats_used ?? 0);
});

const { azureAccounts: getAzureAccounts } = storeToRefs(azureStore);

historyStore.setTotalEmailsAnalyzed(0);
historyStore.setTotalIncidents(0);

const fetchAzureAndLicences = async () => {
  try {
    loading.value = true;
    await azureStore.getAllAzureAccounts(globalOrganisationId.value);
  } finally {
    try {
      if (getAzureAccounts.value.length) {
        await azureStore.numberOfSeats({
          organisation: globalOrganisationId.value,
          azureID: getAzureAccounts.value[0].id,
        });
      }
    } finally {
      loading.value = false;
    }
  }
};
fetchAzureAndLicences();

const fetchIncidentsAndEmails = async () => {
  try {
    loading.value = true;

    const params = {
      start_date: props.startDate,
      end_date: dayjs().format("YYYY-MM-DD"),
      organisationType: CUSTOMER,
    };

    await Promise.allSettled([
      historyStore.getTotalEmailsAnalyzed({
        organisationId: globalOrganisationId.value,
        params,
      }),
      historyStore.getTotalIncidents({
        organisationId: globalOrganisationId.value,
        params,
      }),
      licenceStore.listAllLicences({
        uuid: globalOrganisationId.value,
      }),
    ]);
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.startDate,
  () => {
    fetchIncidentsAndEmails();
  },
  { immediate: true },
);
</script>
